import React from 'react';
import { motion } from 'framer-motion';
import { Image as ImageIcon, Play, Camera } from 'lucide-react';
interface PlaceholderProps {
  type?: 'image' | 'video' | 'gallery';
  aspectRatio?: 'video' | 'square' | 'portrait' | 'auto';
  label?: string;
  className?: string;
}
export function Placeholder({
  type = 'image',
  aspectRatio = 'video',
  label,
  className = ''
}: PlaceholderProps) {
  const aspectClasses = {
    video: 'aspect-video',
    square: 'aspect-square',
    portrait: 'aspect-[3/4]',
    auto: 'h-full w-full min-h-[200px]'
  };
  const Icon = type === 'video' ? Play : type === 'gallery' ? Camera : ImageIcon;
  return <div className={`relative overflow-hidden rounded-xl bg-noronha-graphite border border-noronha-petroleum/40 flex flex-col items-center justify-center group ${aspectClasses[aspectRatio]} ${className}`} data-id="element-274">
      {/* Subtle radial gradient for premium feel */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(31,163,122,0.05)_0%,transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-700" data-id="element-275" />

      <div className="relative z-10 flex flex-col items-center gap-3 text-noronha-petroleum group-hover:text-noronha-emerald/70 transition-colors duration-500" data-id="element-276">
        <Icon className="w-8 h-8 stroke-[1.5]" data-id="element-277" />
        <span className="font-mono text-xs tracking-widest uppercase text-noronha-text-body/50 group-hover:text-noronha-text-body/80 transition-colors duration-500" data-id="element-278">
          {label || `Placeholder ${type}`}
        </span>
      </div>
    </div>;
}
interface SectionHeaderProps {
  eyebrow: string;
  title: string;
  description?: string;
  align?: 'left' | 'center';
  className?: string;
}
export function SectionHeader({
  eyebrow,
  title,
  description,
  align = 'center',
  className = ''
}: SectionHeaderProps) {
  return <div className={`flex flex-col gap-4 ${align === 'center' ? 'items-center text-center' : 'items-start text-left'} ${className}`} data-id="element-279">
      <motion.span initial={{
      opacity: 0,
      y: 10
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase text-noronha-gold" data-id="element-280">
        {eyebrow}
      </motion.span>
      <motion.h2 initial={{
      opacity: 0,
      y: 10
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      delay: 0.1
    }} className="text-3xl md:text-4xl lg:text-5xl text-balance" data-id="element-281">
        {title}
      </motion.h2>
      {description && <motion.p initial={{
      opacity: 0,
      y: 10
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      delay: 0.2
    }} className="text-noronha-text-body max-w-2xl text-lg text-balance" data-id="element-282">
          {description}
        </motion.p>}
    </div>;
}
interface PremiumCardProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}
export function PremiumCard({
  children,
  className = '',
  delay = 0
}: PremiumCardProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay,
    duration: 0.5,
    ease: 'easeOut'
  }} className={`bg-noronha-graphite rounded-2xl border border-noronha-petroleum/30 p-6 md:p-8 hover:border-noronha-emerald/40 hover:-translate-y-1 transition-all duration-300 group ${className}`} data-id="element-283">
      {children}
    </motion.div>;
}
export function Button({
  children,
  variant = 'primary',
  className = '',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'outline';
}) {
  const baseStyles = 'inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-medium transition-all duration-300 text-sm tracking-wide';
  const variants = {
    primary: 'bg-noronha-emerald text-noronha-volcanic hover:bg-noronha-emerald/90 hover:shadow-[0_0_20px_rgba(31,163,122,0.3)]',
    secondary: 'bg-noronha-graphite text-noronha-text-heading border border-noronha-petroleum hover:border-noronha-emerald/50',
    outline: 'bg-transparent text-noronha-text-heading border border-noronha-text-body/30 hover:border-noronha-text-heading'
  };
  return <button className={`${baseStyles} ${variants[variant]} ${className}`} {...props} data-id="element-284">
      {children}
    </button>;
}